/**
 * Crop an image using canvas and return a Blob.
 * @param {{src: string, width?: number, height?: number}} imageSrc
 * @param {{x: number, y: number, width: number, height: number}} crop
 * @returns {Promise<Blob|null>}
 */
export function getCroppedImg(imageSrc, crop) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = imageSrc.src;
    image.crossOrigin = "anonymous";
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = imageSrc.width || crop.width;
      canvas.height = imageSrc.height || crop.height;
      const ctx = canvas.getContext("2d");

      ctx.drawImage(
        image,
        crop.x,
        crop.y,
        crop.width,
        crop.height,
        0,
        0,
        canvas.width,
        canvas.height
      );

      canvas.toBlob((blob) => {
        resolve(blob);
      }, "image/jpeg");
    };
    image.onerror = (error) => {
      reject(error);
    };
  });
}

/**
 * Load an image with cross-origin enabled.
 * @param {string} src
 * @returns {Promise<HTMLImageElement>}
 */
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
    img.src = src;
  });
}

/**
 * Internal helper to wrap text on canvas.
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} text
 * @param {number} maxWidth
 * @returns {string[]}
 */
function wrapText(ctx, text, maxWidth) {
  const words = text.split(/\s+/);
  const lines = [];
  let currentLine = "";

  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    const testLine = currentLine ? currentLine + " " + word : word;
    const metrics = ctx.measureText(testLine);
    const testWidth = metrics.width;

    if (testWidth > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
}

/**
 * Compose a template + overlay + text into a single image.
 * Returns a data URL and the canvas used for drawing.
 * @param {{
 *  templateSrc: string,
 *  overlayImageSrc?: string | null,
 *  text?: string | null,
 *  overlayConfig?: { x?: number, y?: number, width?: number, height?: number, clipAsCircle?: boolean },
 *  textConfig?: { x?: number, y?: number, maxWidth?: number, fontSize?: number, fontFamily?: string, color?: string, textAlign?: CanvasTextAlign, lineHeight?: number },
 *  canvasWidth?: number,
 *  canvasHeight?: number,
 *  canvas?: HTMLCanvasElement | null
 * }} options
 * @returns {Promise<{ dataUrl: string, canvas: HTMLCanvasElement }>}
 */
/**
 * Compose template + overlay + optional multiple text layers into an image.
 * If textLayers are provided, they will be drawn in order; otherwise, a single text/textConfig is used.
 */
export async function composeTemplateImage(options) {
  const {
    templateSrc,
    overlayImageSrc,
    text,
    overlayConfig = {},
    textConfig = {},
    canvasWidth,
    canvasHeight,
    canvas: providedCanvas,
  } = options;

  if (!templateSrc) {
    throw new Error("templateSrc is required");
  }

  const canvas = providedCanvas || document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas context not available");
  }

  const templateImage = await loadImage(templateSrc);

  const w = canvasWidth || templateImage.width;
  const h = canvasHeight || templateImage.height;
  canvas.width = w;
  canvas.height = h;

  let overlayImg = null;
  if (overlayImageSrc) {
    try {
      overlayImg = await loadImage(overlayImageSrc);
    } catch (err) {
      console.warn("Failed to load overlay image", err);
      overlayImg = null;
    }
  }

  const {
    x: overlayX = 0,
    y: overlayY = 0,
    width: overlayWidth,
    height: overlayHeight,
    clipAsCircle = false,
  } = overlayConfig;

  const {
    x: textX = 0,
    y: textY = 0,
    maxWidth: textMaxWidth,
    fontSize = 24,
    fontFamily = "Arial, sans-serif",
    color = "#000000",
    textAlign = "left",
    lineHeight = fontSize * 1.2,
  } = textConfig;

  // Draw template
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(templateImage, 0, 0, canvas.width, canvas.height);

  // Draw overlay
  if (overlayImg) {
    const ow = overlayWidth ?? overlayImg.width;
    const oh = overlayHeight ?? overlayImg.height;

    if (clipAsCircle) {
      ctx.save();
      ctx.beginPath();
      const radius = Math.min(ow, oh) / 2;
      ctx.arc(overlayX + ow / 2, overlayY + oh / 2, radius, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(overlayImg, overlayX, overlayY, ow, oh);
      ctx.restore();
    } else {
      ctx.drawImage(overlayImg, overlayX, overlayY, ow, oh);
    }
  }

  // Draw text
  const textEntries = Array.isArray(text)
    ? text
    : text
      ? [{ value: text, config: textConfig }]
      : [];

  textEntries.forEach((entry) => {
    if (!entry || !entry.value) return;
    const {
      x: lx = textX,
      y: ly = textY,
      maxWidth: lMaxWidth = textMaxWidth,
      fontSize: lFontSize = fontSize,
      fontFamily: lFontFamily = fontFamily,
      color: lColor = color,
      textAlign: lTextAlign = textAlign,
      lineHeight: lLineHeight = lineHeight,
    } = entry.config || {};

    ctx.fillStyle = lColor;
    ctx.font = `${lFontSize}px ${lFontFamily}`;
    ctx.textAlign = lTextAlign;

    const allLines = [];
    const paragraphs = String(entry.value).split("\n");

    paragraphs.forEach((paragraph) => {
      if (!paragraph) {
        allLines.push("");
        return;
      }
      if (lMaxWidth) {
        const wrapped = wrapText(ctx, paragraph, lMaxWidth);
        allLines.push(...wrapped);
      } else {
        allLines.push(paragraph);
      }
    });

    allLines.forEach((line, index) => {
      const yPos = ly + index * lLineHeight;
      if (lMaxWidth) {
        ctx.fillText(line, lx, yPos, lMaxWidth);
      } else {
        ctx.fillText(line, lx, yPos);
      }
    });
  });

  const dataUrl = canvas.toDataURL("image/png");
  return { dataUrl, canvas };
}

/**
 * Attempt to fetch a remote image as a blob and return an object URL.
 * Falls back to the original URL on failure.
 * @param {string} url
 * @returns {Promise<string>}
 */
export async function fetchImageAsObjectUrl(url) {
  if (!url || !url.startsWith("http")) return url;
  try {
    const resp = await fetch(url, { mode: "cors" });
    const blob = await resp.blob();
    return URL.createObjectURL(blob);
  } catch (err) {
    console.warn("Failed to fetch image as blob", err);
    return url;
  }
}
